// ============================================================================
// TableScroll — le seul `<table>` de la Console, et la raison pour laquelle il est seul.
//
// LE DÉFAUT QU'IL SUPPRIME (mesuré au navigateur le 2026-09-02, sonde `mesure-layout-console`).
// Sept tableaux étaient posés directement dans `div.dcard {overflow: hidden}`, sans conteneur de
// défilement. À 360 px, le tableau de `/locations` mesurait 569 px : la page ne débordait PAS — et
// c'est exactement ce qui rendait le défaut invisible — mais les colonnes de droite (« Début »,
// « Fin », « État ») étaient **coupées et inatteignables**, sans barre de défilement ni le moindre
// signe. Un écran qui montre moins qu'il ne prétend, sans le dire (§23.0).
//
// ⚠️ CE COMPOSANT REND LE `<table>` LUI-MÊME, il ne l'enveloppe pas. C'est ce qui rend la règle
// MÉCANIQUE : « aucun `<table>` brut dans la Console » se vérifie d'un grep, sans heuristique de
// voisinage. Un composant qui prendrait le tableau en `children` laisserait la balise dans les
// écrans, et la garde devrait deviner si le bon parent est au-dessus — une garde qui devine finit
// par se tromper dans les deux sens.
//
// ⚠️ POURQUOI PAS LE REPLI EN CARTES de la charte (`.dt-root`, `data-label`, sous 768 px) : la
// Console porte son propre `DataTable`, port de sa maquette, qui n'émet ni les classes ni les
// libellés que ce repli exige. L'y amener est un chantier d'UI à part entière ; le défilement, lui,
// rend la donnée ATTEIGNABLE tout de suite. On répare ce qui cache de l'information avant
// d'harmoniser ce qui la présente.
// ============================================================================
import type { CSSProperties, ReactNode, Ref } from 'react';

export interface TableScrollProps {
  /** `<thead>` / `<tbody>` / `<tfoot>` — le contenu du tableau, pas le tableau. */
  children: ReactNode;
  /**
   * Largeur en dessous de laquelle le tableau défile au lieu de se comprimer.
   * ⚠️ Sans plancher, les colonnes se tassent jusqu'à l'illisible plutôt que de défiler : le
   * navigateur préfère toujours écraser une colonne que déborder de son conteneur.
   */
  minWidth?: number;
  /** Styles additionnels du `<table>` (bordures, largeur) — jamais l'`overflow`, qui est à nous. */
  style?: CSSProperties;
  /** La recette du tableau quand une feuille la porte (en-têtes, survol des rangées : un style en ligne n'a pas de survol). */
  className?: string;
  /** Styles du conteneur défilant, quand la carte a besoin d'un rayon ou d'une marge. */
  wrapStyle?: CSSProperties;
  /** Le conteneur défilant, pour qui doit savoir si le tableau DÉBORDE (la colonne d'actions collée du DataTable). */
  scrollRef?: Ref<HTMLDivElement>;
}

/** Un tableau qui DÉFILE au lieu d'être coupé — la seule façon d'écrire un tableau dans la Console. */
export function TableScroll({ children, minWidth, style, className, wrapStyle, scrollRef }: TableScrollProps) {
  return (
    <div ref={scrollRef} style={{ overflowX: 'auto', ...wrapStyle }}>
      <table className={className} style={{ ...(minWidth === undefined ? null : { minWidth }), ...style }}>
        {children}
      </table>
    </div>
  );
}
